import { Button, Form, Input, message } from "antd";
import { Auth } from "aws-amplify";
import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { createAccountAction, getAccountDetails } from "Sdk/Actions";
import { login } from "Store/reducers/Auth/AuthSlice";
import { emailPattern, FormItemRequiredRule } from "Utility/Constants";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEye, faEyeSlash } from "@fortawesome/free-solid-svg-icons";
import { Loader } from "@/components/Loader";
import AuthRightContent from "@/components/AuthRightContent";
import PublicFooter from "@/components/PublicFooter";

function Login() {
  const navigate = useNavigate();
  const [isLoading, setisLoading] = useState(false);
  const dispatch = useDispatch();
  const location = useLocation();

  const [form] = Form.useForm();
  const [loginError, setloginError] = useState(false);

  useEffect(() => {
    let email = location?.state?.email;
    let password = location?.state?.password;
    if (email && password) {
      handleSubmit({
        email,
        password,
      });
    }
    form.setFieldsValue({ email, password });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location]);

  const handleSubmit = async (data) => {
    setisLoading(true);
    Auth.signIn(data?.email, data?.password)
      .then(async (res) => {
        let data = res?.attributes;
        const nextBillingAt = new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString();

        let userData = {
          username: data?.preferred_username,
          email: data?.email,
          name:
            String(data?.preferred_username[0]).toUpperCase() +
            String(data?.preferred_username).slice(1),
          nextBillingAt,
          userType: "trial",
          paidWidgets: ["review-generator", "seo"],
        };
        let isNewUser = res?.attributes?.profile === "false";
        if (isNewUser) {
          await handleCreateUser(userData);
        }
        dispatch(
          login({
            token: res?.signInUserSession.accessToken.jwtToken,
          })
        );
        await getAccountDetails(data?.preferred_username);
        navigate(isNewUser ? "/dashboard?new_signup=true" : "/dashboard");
        setisLoading(false);
      })
      .catch((err) => {
        setisLoading(false);
        setloginError(true);
        if (err?.message === "User is not confirmed.") {
          message.info("Seems Like Account is not verified yet.");
          navigate("/confirm-signup", { state: { email: data?.email } });
        } else message.error(err.message);
      });
  };

  const handleCreateUser = async (input) => {
    return createAccountAction(input)
      .then(async (res) => {
        const user = await Auth.currentAuthenticatedUser();
        await Auth.updateUserAttributes(user, {
          profile: "true",
        });
      })
      .catch((err) => {
        console.log(err);
      });
  };

  if (location?.state?.directLogin && !loginError) {
    return (
      <div className="div-center h-full flex flex-col">
        <Loader />
        <p className="mt-2">
          Hold tight while we sprinkle some digital magic and set up your
          account. 🚀✨
        </p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 flex items-center justify-center p-4">
      <div className="max-w-5xl w-full">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Left side - Content */}
          <div className="space-y-14">
            {/* Header */}
            <div>
              <h1
                className="font-bold text-gray-900 leading-tight"
                style={{ fontSize: "32px" }}
              >
                Login to your Inprofile
              </h1>
              <p className="text-lg text-gray-600 mt-2">
                Good to have you back!
              </p>
            </div>

            {/* Form */}
            <div className="space-y-12">
              <Form
                onFinish={handleSubmit}
                layout="vertical"
                requiredMark={false}
                form={form}
                name="login"
                initialValues={{ remember: true }}
                className="space-y-4"
              >
                <Form.Item
                  name="email"
                  rules={[
                    FormItemRequiredRule,
                    {
                      pattern: emailPattern,
                      message: "Please enter a valid email",
                    },
                  ]}
                >
                  <Input
                    placeholder="Email address"
                    className="py-3 px-4 input-border"
                    size="large"
                  />
                </Form.Item>

                <Form.Item
                  name="password"
                  rules={[FormItemRequiredRule]}
                  className="mb-2"
                >
                  <Input.Password
                    placeholder="Password"
                    className="py-3 px-4 input-border"
                    size="large"
                    iconRender={(visible) => (
                      <FontAwesomeIcon
                        icon={visible ? faEye : faEyeSlash}
                        className="cursor-pointer"
                      />
                    )}
                  />
                </Form.Item>

                <div className="flex justify-start">
                  <div
                    onClick={() => navigate("/reset-password")}
                    className="text-sm text-blue-500 underline cursor-pointer"
                  >
                    Reset Password?
                  </div>
                </div>

                <Button
                  htmlType="submit"
                  type="primary"
                  size="large"
                  loading={isLoading}
                  className="w-full h-12 text-base font-semibold rounded-lg bg-primary mt-6"
                >
                  Sign In
                </Button>
              </Form>
              <div className="text-st mt-6">
                <div
                  className="text-sm hover:underline cursor-pointer"
                  onClick={() => navigate("/signup")}
                >
                  Don't have an account? <span>Sign up</span>
                </div>
              </div>
            </div>
          </div>

          {/* Right side content */}
          <AuthRightContent />
        </div>

        {/* Footer */}
        <PublicFooter />
      </div>
    </div>
  );
}

export default Login;
